import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { User, Lock, Activity, Clock, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function AdminProfile() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");

  return (
    <div data-testid="admin-profile-page">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">My Profile</h1>
      <Tabs defaultValue="profile">
        <TabsList className="bg-white rounded-xl border mb-4"><TabsTrigger value="profile" className="rounded-xl text-xs">Profile</TabsTrigger><TabsTrigger value="password" className="rounded-xl text-xs">Change Password</TabsTrigger><TabsTrigger value="activity" className="rounded-xl text-xs">Activity Log</TabsTrigger><TabsTrigger value="login" className="rounded-xl text-xs">Login History</TabsTrigger><TabsTrigger value="2fa" className="rounded-xl text-xs">2FA</TabsTrigger></TabsList>

        <TabsContent value="profile">
          <div className="bg-white rounded-2xl p-6 shadow-sm border max-w-lg">
            <div className="flex items-center gap-4 mb-6">
              {user?.picture ? <img src={user.picture} alt="" className="w-20 h-20 rounded-full" /> : <div className="w-20 h-20 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600 text-3xl font-bold">{user?.name?.[0]}</div>}
              <div><p className="text-lg font-semibold">{user?.name}</p><p className="text-sm text-slate-500">{user?.email}</p><Badge className="mt-1 bg-indigo-100 text-indigo-700">{user?.role}</Badge></div>
            </div>
            <div className="space-y-4">
              <div><Label className="text-xs">Name</Label><Input value={name} onChange={e => setName(e.target.value)} className="rounded-xl mt-1" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={phone} onChange={e => setPhone(e.target.value)} className="rounded-xl mt-1" /></div>
              <div><Label className="text-xs">Email</Label><Input value={user?.email || ""} disabled className="rounded-xl mt-1 bg-slate-50" /></div>
              <Button onClick={() => toast.success("Profile updated")} className="rounded-full bg-cyan-600 hover:bg-cyan-700">Save Changes</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="password">
          <div className="bg-white rounded-2xl p-6 shadow-sm border max-w-lg space-y-4">
            <div><Label className="text-xs">Current Password</Label><Input type="password" className="rounded-xl mt-1" /></div>
            <div><Label className="text-xs">New Password</Label><Input type="password" className="rounded-xl mt-1" /></div>
            <div><Label className="text-xs">Confirm New Password</Label><Input type="password" className="rounded-xl mt-1" /></div>
            <Button onClick={() => toast.success("Password changed")} className="rounded-full bg-cyan-600 hover:bg-cyan-700">Update Password</Button>
          </div>
        </TabsContent>

        <TabsContent value="activity"><div className="bg-white rounded-2xl p-12 text-center shadow-sm border"><Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Activity Log</p><p className="text-xs text-slate-400 mt-1">Your recent admin actions will be logged here</p></div></TabsContent>
        <TabsContent value="login"><div className="bg-white rounded-2xl p-12 text-center shadow-sm border"><Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Login History</p><p className="text-xs text-slate-400 mt-1">Track all admin login sessions with IP and device info</p></div></TabsContent>
        <TabsContent value="2fa">
          <div className="bg-white rounded-2xl p-6 shadow-sm border max-w-lg">
            <div className="flex items-center justify-between p-4 bg-sky-50 rounded-xl"><div className="flex items-center gap-3"><Shield className="w-6 h-6 text-cyan-600" /><div><p className="text-sm font-semibold">Two-Factor Authentication</p><p className="text-xs text-slate-500">Add an extra layer of security to your account</p></div></div><Switch /></div>
            <p className="text-xs text-slate-400 mt-4">When enabled, you'll need to enter a verification code from your authenticator app each time you log in.</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
